<script setup lang="ts">
import { PortableText, toPlainText } from '@portabletext/vue';
import type { PortableTextComponents } from '@portabletext/vue';
import type { TypedObject } from '@portabletext/types';
import { computed, h } from 'vue';
import { generateSpotlightSlug } from '@/lib/spotlightAnchor';

const props = defineProps<{
  value: unknown[] | null | undefined;
}>();

const blocks = computed(() => (props.value || []) as TypedObject[]);

function headingId(blockValue: unknown): string {
  try {
    const text = toPlainText(blockValue as Parameters<typeof toPlainText>[0]);
    return generateSpotlightSlug(text || 'heading');
  } catch {
    return 'heading';
  }
}

const components: PortableTextComponents = {
  types: {
    image: ({ value }) => {
      const url = (value as { url?: string })?.url;
      const alt = (value as { alt?: string })?.alt ?? '';
      const caption = (value as { caption?: string })?.caption?.trim() ?? '';
      if (!url) return h('div', { class: 'my-8 text-sm text-black/40' }, '[Image missing]');
      return h('figure', { class: 'my-8 w-full' }, [
        h('div', { class: 'rounded-lg overflow-hidden bg-black/5' }, [
          h('img', {
            src: url,
            alt,
            class: 'w-full h-auto block',
            loading: 'lazy',
            decoding: 'async',
          }),
        ]),
        caption
          ? h('figcaption', { class: 'mt-2 text-sm text-center text-black/50' }, caption)
          : null,
      ]);
    },
    spotlightYoutube: ({ value }) => {
      const rawUrl = (value as { url?: string })?.url ?? '';
      const caption = (value as { caption?: string })?.caption ?? '';
      const videoIdMatch =
        rawUrl.match(/youtu\.be\/([^?&#]+)/) ||
        rawUrl.match(/youtube\.com\/(?:watch\?v=|embed\/)([^?&#]+)/);
      const videoId = videoIdMatch?.[1] ?? '';
      if (!videoId) return h('div', { class: 'my-8 text-sm text-black/40' }, '[Video missing]');
      const embedUrl = `https://www.youtube.com/embed/${videoId}`;
      return h('figure', { class: 'my-8 w-full' }, [
        h('div', { class: 'relative w-full', style: 'padding-top: 56.25%' }, [
          h('iframe', {
            src: embedUrl,
            title: caption || 'YouTube video',
            frameborder: '0',
            allow:
              'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share',
            allowfullscreen: true,
            class: 'absolute inset-0 w-full h-full rounded-lg',
            loading: 'lazy',
          }),
        ]),
        caption
          ? h('figcaption', { class: 'mt-2 text-sm text-center text-black/50' }, caption)
          : null,
      ]);
    },
    spotlightTable: ({ value }) => {
      const headers = (value as { headers?: string[] })?.headers ?? [];
      const rows = (value as { rows?: { cells?: string[] }[] })?.rows ?? [];
      const cellBase = 'px-4 py-3 border-b border-black/10 align-top text-[0.9375rem] leading-snug';
      return h(
        'div',
        { class: 'my-8 overflow-x-auto rounded-xl border border-black/10 bg-white shadow-sm' },
        [
          h('table', { class: 'w-full min-w-[min(100%,520px)] text-sm border-collapse' }, [
            h('thead', {}, [
              h(
                'tr',
                {},
                headers.map((header, hi) =>
                  h(
                    'th',
                    {
                      class: [
                        cellBase,
                        'border-b border-black/20 bg-black/[0.04] text-left font-normal text-black/85',
                        hi === 0 ? 'w-[22%] text-black/55' : '',
                      ]
                        .filter(Boolean)
                        .join(' '),
                    },
                    header === '' ? '\u00a0' : header
                  )
                )
              ),
            ]),
            h(
              'tbody',
              {},
              rows.map((row, i) =>
                h(
                  'tr',
                  { class: i % 2 === 1 ? 'bg-black/[0.02]' : '' },
                  (row.cells ?? []).map((cell, ci) =>
                    h(
                      'td',
                      {
                        class: [
                          cellBase,
                          ci === 0
                            ? 'text-black/75 align-top max-w-[12rem] font-normal'
                            : 'text-black/90 font-normal',
                        ].join(' '),
                      },
                      cell
                    )
                  )
                )
              )
            ),
          ]),
        ]
      );
    },
  },
  marks: {
    strong: (_, { slots }) => h('strong', { class: 'font-bold text-inherit' }, slots.default?.()),
    link: ({ value }, { slots }) => {
      const href = (value as { href?: string })?.href || '#';
      const target = (value as { target?: string })?.target;
      const external = /^https?:\/\//i.test(href);
      return h(
        'a',
        {
          href,
          target: target || (external ? '_blank' : undefined),
          rel: external ? 'nofollow noreferrer' : undefined,
          class:
            'spotlight-pt-link text-black no-underline border-b border-black/15 pb-[1px] font-bold',
        },
        slots.default?.()
      );
    },
  },
  block: {
    normal: (_, { slots }) => h('p', { class: 'mb-6 leading-relaxed' }, slots.default?.()),
    h2: ({ value }, { slots }) =>
      h(
        'h2',
        {
          id: headingId(value),
          class: 'font-bold mt-12 mb-6 scroll-mt-24 text-[2rem] md:text-[2.5rem] leading-[1.1]',
        },
        slots.default?.()
      ),
    h3: ({ value }, { slots }) =>
      h(
        'h3',
        {
          id: headingId(value),
          class: 'font-bold mt-10 mb-5 scroll-mt-24 text-2xl',
        },
        slots.default?.()
      ),
    h4: ({ value }, { slots }) =>
      h(
        'h4',
        {
          id: headingId(value),
          class: 'font-bold mt-8 mb-4 scroll-mt-24 text-xl',
        },
        slots.default?.()
      ),
    blockquote: (_, { slots }) =>
      h(
        'blockquote',
        {
          class: 'border-l-4 border-black/20 pl-4 my-6 italic text-black/80',
        },
        slots.default?.()
      ),
  },
  list: {
    bullet: (_, { slots }) =>
      h(
        'ul',
        {
          class:
            'spotlight-pt-list-bullet not-prose mb-6 ml-0 list-none space-y-3.5 pl-0 leading-relaxed',
        },
        slots.default?.()
      ),
    number: (_, { slots }) =>
      h(
        'ol',
        { class: 'spotlight-pt-list-number not-prose mb-6 ml-0 space-y-3.5 pl-0 leading-relaxed' },
        slots.default?.()
      ),
  },
};
</script>

<template>
  <div class="spotlight-pt-root">
    <PortableText
      v-if="blocks.length"
      :value="blocks"
      :components="components"
      :on-missing-component="false"
    />
  </div>
</template>

<style scoped>
/* Middle-dot lists: keep the dot in the first text line's vertical center. */
.spotlight-pt-root :deep(.spotlight-pt-list-bullet) {
  list-style: none;
  margin-left: 1.25rem;
  padding-left: 0;
  font-size: inherit;
  line-height: 1.75;
}
@media (min-width: 768px) {
  .spotlight-pt-root :deep(.spotlight-pt-list-bullet) {
    margin-left: 1.75rem;
  }
}
.spotlight-pt-root :deep(.spotlight-pt-list-bullet > li) {
  position: relative;
  display: block;
  margin: 0 0 0.6rem;
  padding-left: 1rem;
  list-style: none;
}
.spotlight-pt-root :deep(.spotlight-pt-list-bullet > li:last-child) {
  margin-bottom: 0;
}
.spotlight-pt-root :deep(.spotlight-pt-list-bullet > li)::before {
  content: '\00b7';
  position: absolute;
  left: 0;
  top: 0;
  display: flex;
  width: 0.5rem;
  height: 1.75em;
  align-items: center;
  justify-content: center;
  font-size: 1em;
  line-height: 1;
  font-weight: 600;
  color: rgb(0 0 0 / 0.48);
  transform: scale(1.25);
  transform-origin: center;
}
@media (min-width: 768px) {
  .spotlight-pt-root :deep(.spotlight-pt-list-bullet > li) {
    margin-bottom: 0.65rem;
  }
}
/* PT wraps list content in p.mb-6 — reset inside list items */
.spotlight-pt-root :deep(.spotlight-pt-list-bullet > li > p) {
  margin: 0;
}
.spotlight-pt-root :deep(.spotlight-pt-list-bullet > li > p + p) {
  margin-top: 0.45rem;
}
.spotlight-pt-root :deep(.spotlight-pt-list-bullet > li > ul),
.spotlight-pt-root :deep(.spotlight-pt-list-bullet > li > ol) {
  margin-top: 0.5rem;
  margin-bottom: 0;
  margin-left: 0.25rem;
  padding-left: 0.65rem;
}

/* Numbered lists use the same article rhythm, with "1、" markers. */
.spotlight-pt-root :deep(.spotlight-pt-list-number) {
  list-style: none;
  margin-left: 1.25rem;
  padding-left: 0;
  font-size: inherit;
  line-height: 1.75;
  counter-reset: spotlight-pt-number;
}
@media (min-width: 768px) {
  .spotlight-pt-root :deep(.spotlight-pt-list-number) {
    margin-left: 1.75rem;
  }
}
.spotlight-pt-root :deep(.spotlight-pt-list-number > li) {
  position: relative;
  display: block;
  margin: 0 0 0.6rem;
  padding-left: 1.5rem;
  list-style: none;
  counter-increment: spotlight-pt-number;
}
.spotlight-pt-root :deep(.spotlight-pt-list-number > li:last-child) {
  margin-bottom: 0;
}
.spotlight-pt-root :deep(.spotlight-pt-list-number > li)::before {
  content: counter(spotlight-pt-number) '、';
  position: absolute;
  left: 0;
  top: 0;
  display: flex;
  height: 1.75em;
  align-items: center;
  justify-content: flex-start;
  font-size: 1em;
  line-height: 1;
  font-weight: 600;
  color: rgb(0 0 0 / 0.55);
}
.spotlight-pt-root :deep(.spotlight-pt-list-number > li > p) {
  margin: 0;
}
.spotlight-pt-root :deep(.spotlight-pt-list-number > li > p + p) {
  margin-top: 0.45rem;
}

/* Any real link in PT (mark link, pasted HTML, portable edge cases) */
.spotlight-pt-root :deep(a[href]) {
  font-weight: 700 !important;
  color: rgb(0 0 0) !important;
  text-decoration: none !important;
  border-bottom: 1px solid rgb(0 0 0 / 0.15);
  padding-bottom: 1px;
}
/* Prose often sets weight on inner spans — without this, link text stays “normal” weight */
.spotlight-pt-root :deep(a[href] *) {
  font-weight: inherit !important;
}
.spotlight-pt-root :deep(a[href] span) {
  font-weight: 700 !important;
}
.spotlight-pt-root :deep(a[href]:hover) {
  border-bottom-color: rgb(0 0 0 / 0.4);
}

/* Sanity strong marks should render as real bold text. */
.spotlight-pt-root :deep(strong) {
  font-weight: 700;
}
.spotlight-pt-root :deep(a[href] strong) {
  font-weight: 700 !important;
}
.spotlight-pt-root :deep(h2),
.spotlight-pt-root :deep(h3),
.spotlight-pt-root :deep(h4),
.spotlight-pt-root :deep(h5),
.spotlight-pt-root :deep(h6),
.spotlight-pt-root :deep(h2 *),
.spotlight-pt-root :deep(h3 *),
.spotlight-pt-root :deep(h4 *),
.spotlight-pt-root :deep(h5 *),
.spotlight-pt-root :deep(h6 *) {
  font-weight: 700 !important;
}
</style>
